'use client';

import * as React from 'react';
import Image from 'next/image';
import { Typography } from '@/components/ui/typography';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { useTranslations } from 'next-intl';

interface ReviewsSectionProps {
  showImages?: boolean;
}

const PLATFORMS = [
  {
    name: 'Capterra',
    logoLight: '/images/home/capture-img.webp',
    logoDark: '/images/home/capture-white-img.webp',
  },
  {
    name: 'GoodFirms',
    logoLight: '/images/home/goodfirm-img.webp',
    logoDark: '/images/home/goodfirm-white-img.webp',
  },
  {
    name: 'Software Advice',
    logoLight: '/images/home/sofware-advice-img.webp',
    logoDark: '/images/home/sofware-advice-white-img.webp',
  },
  {
    name: 'SoftwareSuggest',
    logoLight: '/images/home/software-suggest-img.webp',
    logoDark: '/images/home/software-suggest-white-img.webp',
  },
  {
    name: 'Google Reviews',
    logoLight: '/images/home/google-review-img.webp',
    logoDark: '/images/home/google-review-white-img.webp',
  },
];

export function ReviewsSection({ showImages = true }: ReviewsSectionProps) {
  const t = useTranslations('homeReviews');
  const sectionRef = useScrollAnimation({ threshold: 0.15 });

  return (
    <SectionWrapper
      ref={sectionRef.ref}
      variant="gradient"
      padding="sm"
      className="relative"
    >
      {/* Background accents */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-primary-500/5 blur-3xl" />
        <div className="absolute right-1/4 bottom-0 h-96 w-96 rounded-full bg-blue-500/5 blur-3xl" />
      </div>

      <div className="relative z-10">
        <div
          className={`mx-auto transition-all duration-700 ${sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
        >
          {/* Review Platforms Grid */}
          <div className="flex flex-wrap md:grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8 mb-8 items-center justify-center">
            {PLATFORMS.map((platform, index) => (
              <div
                key={index}
                className="flex flex-col justify-center items-center text-center group"
                style={{
                  transitionDelay: `${index * 100}ms`,
                }}
              >
                {/* Platform Logo */}
                {showImages && (
                  <div className="relative h-24 md:h-28 w-full flex items-center justify-center">
                    <Image
                      src={platform.logoLight}
                      alt={`${platform.name} logo`}
                      width={200}
                      height={80}
                      className="h-12 md:h-16 w-auto object-contain dark:hidden opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                      loading="lazy"
                      quality={70}
                      sizes="(max-width: 640px) 120px, (max-width: 768px) 150px, 200px"
                    />
                    <Image
                      src={platform.logoDark}
                      alt={`${platform.name} logo`}
                      width={200}
                      height={80}
                      className="h-12 md:h-16 w-auto object-contain hidden dark:block opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                      loading="lazy"
                      quality={70}
                      sizes="(max-width: 640px) 120px, (max-width: 768px) 150px, 200px"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Summary Text */}
          <div className="text-center">
            <Typography variant="p" className="text-md opacity-80">
              {t('summaryText')}
            </Typography>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}

